import React, { useState, useEffect } from 'react';
import { 
  AppBar, 
  Toolbar, 
  IconButton, 
  Badge, 
  Box,
  Button,
  Menu,
  MenuItem,
  Avatar,
  Divider,
  Container
} from '@mui/material';
import { 
  Download as DownloadIcon, 
  ShoppingCart as CartIcon,
  Favorite as FavoriteIcon,
  AccountCircle as AccountIcon,
  KeyboardArrowDown as ArrowDownIcon,
  Menu as MenuIcon,
  CloudUpload
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';

interface AppHeaderProps {
  downloadCount: number;
  isAuthenticated: boolean;
  onLoginClick: () => void;
  onLogout: () => void;
  userName?: string;
  onLogoClick: () => void;
  cartItemCount?: number;
  onCartClick?: () => void;
  onFavoritesClick?: () => void;
  onDownloadsClick?: () => void;
  user?: { role?: string } | null;
}

const AppHeader: React.FC<AppHeaderProps> = ({ 
  downloadCount, 
  isAuthenticated,
  onLoginClick,
  onLogout,
  userName,
  onLogoClick,
  cartItemCount = 0,
  onCartClick = () => {},
  onFavoritesClick = () => {},
  onDownloadsClick = () => {},
  user = null
}) => {
  const navigate = useNavigate();
  const [userMenuAnchor, setUserMenuAnchor] = useState<null | HTMLElement>(null);
  const [mobileMenuAnchor, setMobileMenuAnchor] = useState<null | HTMLElement>(null);
  const [isVisible, setIsVisible] = useState(true);
  const [lastScrollY, setLastScrollY] = useState(0);

  // Scroll direction detection
  useEffect(() => {
    const handleScroll = () => {
      const currentScrollY = window.scrollY;
      
      // Show header when scrolling up or at the top
      if (currentScrollY < lastScrollY || currentScrollY < 100) {
        setIsVisible(true);
      } 
      // Hide header when scrolling down and not at the top
      else if (currentScrollY > lastScrollY && currentScrollY > 100) {
        setIsVisible(false);
      }
      
      setLastScrollY(currentScrollY);
    };

    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, [lastScrollY]);

  const handleUserMenuOpen = (event: React.MouseEvent<HTMLElement>) => {
    setUserMenuAnchor(event.currentTarget);
  };

  const handleUserMenuClose = () => {
    setUserMenuAnchor(null);
  };

  const handleMobileMenuOpen = (event: React.MouseEvent<HTMLElement>) => {
    setMobileMenuAnchor(event.currentTarget);
  };

  const handleMobileMenuClose = () => {
    setMobileMenuAnchor(null);
  };

  // Navigation handlers
  const handleBrowseClick = () => {
    navigate('/products');
  };


  const handleAboutClick = () => {
    navigate('/about');
  };

  const handleAdminUploadClick = () => {
    navigate('/admin/upload');
  };

  const handleProfileClick = () => {
    handleUserMenuClose();
    navigate('/profile');
  };

  const handleDownloadsClick = () => {
    handleUserMenuClose();
    navigate('/downloads');
  };

  const handleFavoritesClick = () => {
    handleUserMenuClose();
    navigate('/favorites');
  };



  return (
    <>
      {/* Main Header */}
      <AppBar 
        position="fixed" 
        elevation={0}
        sx={{ 
          bgcolor: 'white',
          borderBottom: '1px solid',
          borderColor: 'grey.200',
          transform: isVisible ? 'translateY(0)' : 'translateY(-100%)',
          transition: 'transform 0.3s ease-in-out',
          top: 0,
          left: 0,
          right: 0,
          zIndex: 1100
        }}
      >
        <Container maxWidth="lg">
          <Toolbar sx={{ 
            px: 0, 
            py: 1,
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            position: 'relative'
          }}>
            {/* Logo */}
            <Button
              onClick={onLogoClick}
              sx={{ 
                flexGrow: 0,
                fontWeight: 700,
                color: 'primary.main',
                mr: { xs: 1, md: 4 },
                textTransform: 'none',
                fontSize: { xs: '1.2rem', md: '1.5rem' },
                p: 0,
                minWidth: 'auto',
                display: 'flex',
                alignItems: 'center',
                gap: 1,
                '&:hover': {
                  bgcolor: 'transparent',
                  color: 'primary.dark'
                }
              }}
            >
              <Avatar
                src={`${process.env.REACT_APP_API_URL || 'http://localhost:5001/api'}/image/artful`}
                alt="ArtFul Logo"
                sx={{ 
                  width: { xs: 32, md: 40 },
                  height: { xs: 32, md: 40 },
                  border: '2px solid',
                  borderColor: 'primary.main'
                }}
              />
              <Box sx={{ display: { xs: 'none', sm: 'block' } }}>
                ArtFul
              </Box>
            </Button>

            {/* Navigation Menu - Hidden on Mobile, centered on desktop */}
            <Box sx={{ 
              display: { xs: 'none', md: 'flex' }, 
              gap: 2,
              position: 'absolute',
              left: '50%',
              transform: 'translateX(-50%)',
              alignItems: 'center'
            }}>
              <Button 
                color="inherit" 
                onClick={handleBrowseClick}
                sx={{ 
                  color: 'text.primary',
                  fontWeight: 500,
                  '&:hover': { bgcolor: 'grey.100' }
                }}
              >
                Browse SVGs
              </Button>
              <Button 
                color="inherit" 
                onClick={handleAboutClick}
                sx={{ 
                  color: 'text.primary',
                  fontWeight: 500,
                  '&:hover': { bgcolor: 'grey.100' }
                }}
              >
                About
              </Button>
              
              {/* Admin Upload - Only show for admins */}
              {user?.role === 'admin' && (
                <Button 
                  color="inherit" 
                  onClick={handleAdminUploadClick}
                  startIcon={<CloudUpload />}
                  sx={{ 
                    color: 'primary.main',
                    fontWeight: 600,
                    '&:hover': { bgcolor: 'primary.50' }
                  }}
                >
                  Admin Upload
                </Button>
              )}
            </Box>

            {/* Right Side Actions - Pushed to the right */}
            <Box sx={{ 
              display: 'flex', 
              alignItems: 'center', 
              gap: { xs: 0.5, md: 1 },
              ml: 'auto'
            }}>
              {/* Favorites - Visible on all screen sizes */}
              <IconButton 
                color="inherit"
                onClick={onFavoritesClick}
                sx={{ 
                  color: 'text.secondary',
                  display: 'flex',
                  '&:hover': { color: 'primary.main' }
                }}
              >
                <FavoriteIcon />
              </IconButton>

              {/* Downloads */}
              <IconButton 
                color="inherit"
                onClick={handleDownloadsClick}
                sx={{ 
                  color: 'text.secondary',
                  '&:hover': { color: 'primary.main' }
                }}
              >
                <Badge badgeContent={downloadCount} color="primary">
                  <DownloadIcon />
                </Badge>
              </IconButton>

              {/* Cart */}
              <IconButton 
                color="inherit"
                onClick={onCartClick}
                sx={{ 
                  color: 'text.secondary',
                  '&:hover': { color: 'primary.main' }
                }}
              >
                <Badge badgeContent={cartItemCount} color="primary">
                  <CartIcon />
                </Badge>
              </IconButton>

              <Divider orientation="vertical" flexItem sx={{ 
                mx: { xs: 0.5, md: 1 },
                display: { xs: 'none', sm: 'block' }
              }} />

              {/* User Menu / Login Button - Responsive */}
              {isAuthenticated ? (
                <Button
                  onClick={handleUserMenuOpen}
                  sx={{ 
                    color: 'text.primary',
                    textTransform: 'none',
                    fontWeight: 500,
                    display: { xs: 'none', sm: 'flex' },
                    '&:hover': { bgcolor: 'grey.100' }
                  }}
                  endIcon={<ArrowDownIcon />}
                >
                  <Avatar 
                    sx={{ 
                      width: 32, 
                      height: 32, 
                      mr: 1,
                      bgcolor: 'primary.main'
                    }}
                  >
                    <AccountIcon />
                  </Avatar>
                  {userName || 'My Account'}
                </Button>
              ) : (
                <Button
                  onClick={onLoginClick}
                  variant="contained"
                  sx={{ 
                    fontWeight: 600,
                    borderRadius: 2,
                    px: { xs: 2, md: 3 },
                    fontSize: { xs: '0.8rem', md: '1rem' }
                  }}
                >
                  Sign In
                </Button>
              )}

              {/* Mobile Menu Button */}
              <IconButton
                color="inherit"
                onClick={handleMobileMenuOpen}
                sx={{ 
                  display: { xs: 'flex', md: 'none' },
                  color: 'text.primary',
                  ml: 1
                }}
              >
                <MenuIcon />
              </IconButton>
            </Box>
          </Toolbar>
        </Container>

        {/* User Menu Dropdown */}
        <Menu
          anchorEl={userMenuAnchor}
          open={Boolean(userMenuAnchor)}
          onClose={handleUserMenuClose}
          PaperProps={{
            sx: {
              mt: 1,
              minWidth: 200,
              boxShadow: 3,
              borderRadius: 2,
            }
          }}
        >
          {/* User Menu Header with Logo */}
          <Box sx={{ 
            p: 2, 
            borderBottom: '1px solid', 
            borderColor: 'grey.200',
            display: 'flex',
            alignItems: 'center',
            gap: 2
          }}>
            <Avatar
              src={`${process.env.REACT_APP_API_URL || 'http://localhost:5001/api'}/image/artful`}
              alt="ArtFul Logo"
              sx={{ 
                width: 24,
                height: 24,
                border: '1px solid',
                borderColor: 'primary.main'
              }}
            />
            <Box sx={{ fontWeight: 600, color: 'primary.main', fontSize: '0.9rem' }}>
              ArtFul Account
            </Box>
          </Box>
          <MenuItem onClick={handleProfileClick}>
            <AccountIcon sx={{ mr: 2 }} />
            Profile
          </MenuItem>
          <MenuItem onClick={handleDownloadsClick}>
            <DownloadIcon sx={{ mr: 2 }} />
            My Downloads
          </MenuItem>
          <MenuItem onClick={handleFavoritesClick}>
            <FavoriteIcon sx={{ mr: 2 }} />
            Favorites
          </MenuItem>
          
          {/* Admin Upload - Only show for admins */}
          {user?.role === 'admin' && (
            <>
              <Divider />
              <MenuItem onClick={() => {
                handleUserMenuClose();
                handleAdminUploadClick();
              }}>
                <CloudUpload sx={{ mr: 2 }} />
                Admin Upload
              </MenuItem>
            </>
          )}
          
          <Divider />
          <MenuItem onClick={handleUserMenuClose}>
            Settings
          </MenuItem>
                  <MenuItem onClick={() => {
          handleUserMenuClose();
          onLogout();
        }}>
          Sign Out
        </MenuItem>
        </Menu>

        {/* Mobile Menu */}
        <Menu
          anchorEl={mobileMenuAnchor}
          open={Boolean(mobileMenuAnchor)}
          onClose={handleMobileMenuClose}
          PaperProps={{
            sx: {
              mt: 1,
              minWidth: 250,
              boxShadow: 3,
              borderRadius: 2,
            }
          }}
        >
          {/* Mobile Menu Header with Logo */}
          <Box sx={{ 
            p: 2, 
            borderBottom: '1px solid', 
            borderColor: 'grey.200',
            display: 'flex',
            alignItems: 'center',
            gap: 2
          }}>
            <Avatar
              src={`${process.env.REACT_APP_API_URL || 'http://localhost:5001/api'}/image/artful`}
              alt="ArtFul Logo"
              sx={{ 
                width: 32,
                height: 32,
                border: '2px solid',
                borderColor: 'primary.main'
              }}
            />
            <Box sx={{ fontWeight: 600, color: 'primary.main' }}>
              ArtFul
            </Box>
          </Box>
          <MenuItem onClick={() => {
            handleMobileMenuClose();
            handleBrowseClick();
          }}>
            Browse SVGs
          </MenuItem>
          <MenuItem onClick={() => {
            handleMobileMenuClose();
            handleAboutClick();
          }}>
            About
          </MenuItem>
          <MenuItem onClick={() => {
            handleMobileMenuClose();
            onCartClick();
          }}>
            <CartIcon sx={{ mr: 2 }} />
            Cart ({cartItemCount})
          </MenuItem>
          <Divider />
          {isAuthenticated ? (
            <>
              <MenuItem onClick={() => { handleMobileMenuClose(); navigate('/profile'); }}>
                <AccountIcon sx={{ mr: 2 }} />
                Profile
              </MenuItem>
              <MenuItem onClick={() => {
                handleMobileMenuClose();
                navigate('/downloads');
              }}>
                <DownloadIcon sx={{ mr: 2 }} />
                My Downloads
              </MenuItem>
              <MenuItem onClick={() => {
                handleMobileMenuClose();
                navigate('/favorites');
              }}>
                <FavoriteIcon sx={{ mr: 2 }} />
                Favorites
              </MenuItem>
              
              {/* Admin Upload - Only show for admins */}
              {user?.role === 'admin' && (
                <>
                  <Divider />
                  <MenuItem onClick={() => {
                    handleMobileMenuClose();
                    handleAdminUploadClick();
                  }}>
                    <CloudUpload sx={{ mr: 2 }} />
                    Admin Upload
                  </MenuItem>
                </>
              )}
              
              <Divider />
              <MenuItem onClick={() => {
                handleMobileMenuClose();
                onLogout();
              }}>
                Sign Out
              </MenuItem>
            </>
          ) : (
            <MenuItem onClick={() => {
              handleMobileMenuClose();
              onLoginClick();
            }}>
              Sign In
            </MenuItem>
          )}
        </Menu>
      </AppBar>

      {/* Spacer to prevent content jumping when header is fixed */}
      <Box sx={{ height: { xs: 64, md: 72 } }} />

    </>
  );
};

export default AppHeader; 